/* -------------------------------------------------------------------------------------
Vuex module to manage tags data

Main components
---------------
mutations:
- CREATE_EMPTY_PARTICIPANT: pushes empty participant to store
- SET_PARTICIPANTS: set the entire participants array in store
- SET_PARTICIPANTS_STATS: gets the participants stats and pushes them to store
- SET_PARTICIPANT: Sets the single participant

actions:
- createEmptyParticipant: Pushes an empty participant object to store
- deleteParticipant: Deletes participant from DB & store
- fetchParticipants: Gets all participants from the API and update corresponding arrays in vuex store
- fetchParticipant: Get specific participant details from participants array and push it to vuex store
- fetchParticipantsStats: Fetch the participants stats from DB and push them to store
- insertParticipant: Inserts new participant via API into DB
- updateParticipant: Update the current participant in the DB 
- updateStoreParticipants: add or update participant in participants array 
  
getters:
- getParticipantById: Finds given participant in participants
  
TASKS:
- dispatch('fetchParticipants'): delete participant from state instead of re-fetching (see action updateStoreParticipants)
- dispatch('fetchParticipants'): insert participant into state instead of re-fetching (see action updateStoreParticipants)
---------------------------------------------------------------------------------------*/

// import tourdb library
import ApiService from '@/services/ApiService.js'

export const namespaced = true

export const state = {
  tags: [],
  tag: {},
}

export const mutations = {

  // Pushes all participants fetched from DB to store
  SET_TAGS(state, tags) {
    state.tags = tags
  },

  SET_TAG(state, tag) {
    state.tag = tag
  }
}

export const actions = {

  // STANDARD STORE ACTIONS
  // ----------------------
  // Deletes current participant from DB & store
  deleteTag({ dispatch }) {

    ApiService.deleteTag(this.state.tag.tag.id)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The tag was successfully deleted'
        }
        dispatch('notification/add', notification, { root: true })

        // after the deletion the participants array is fetched again
        dispatch('fetchTags')
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error deleting the tag (potentially referenced in other objects): ' +
            error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Gets all participants from the API and pushes them to the store
  fetchTags({ commit, dispatch }) {

    ApiService.getTags()
      .then(response => {
        commit('SET_TAGS', response.data.tags)
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error getting the tags: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Get specific tag details from tags array and push it to vuex store
  fetchTag({ commit, getters }, id) {
    
    var tag = getters.getTagById(id) // See if we already have this tag in the tags store

    commit('SET_TAG', tag) // push tag to store
  },

  // Inserts new tag via API into DB
  insertTag({ dispatch }) {
   
    // send new tag via API to DB
    ApiService.insertTag(this.state.tag.tag)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The tag was successfully inserted'
        }
        dispatch('notification/add', notification, { root: true })
        
        // after the insert the participants array is fetched again
        dispatch('fetchTags')
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error inserting the tag: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Update the current tag in the DB 
  updateParticipant({ dispatch }, participantId) {

    // update tag via API
    ApiService.updateTag(
      tagId,
      this.state.tag.tag
    )
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The tag was successfully saved'
        }
        dispatch('notification/add', notification, { root: true })

        // after the update the participants array is fetched again
        dispatch('fetchTags')
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error saving the tag: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Add or update tag in tags array --> ACTION: Use this instead of re-fetch of participants 
  updateStoreParticipants({ commit }, { participantId }) {

    let curTags = JSON.parse(
      JSON.stringify(this.state.tag.tags)
    )
    
    let newTags = []
    if (tagId) {
      // if tag id exists it's an update operation
      curTags.forEach(record => {
        // loop through existing tags array
        if (record.id == tagId) {
          // if current tag is the changed tag
          newTags.push(
            JSON.parse(JSON.stringify(this.state.tag.tag))
          ) // the tag is being replaced
        } else {
          newTags.push(record) // the existing tag is pushed out
        }
      })
    
    } else {
      // if no tagId exists, it's an insert operation
      curTags.push(
        JSON.parse(JSON.stringify(this.state.tag.tag))
      )
      newTags = curTags
    }
    commit('SET_TAGS', newTags)
  },
}

export const getters = {

  // Finds given tag in tags
  getTagById: state => id => {
    let tags = JSON.parse(JSON.stringify(state))
    return tags.tags.find(
      tag => tag.id === id.id
    )
  }
}
